import React, { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  isPushSupported,
  getPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed,
} from '../utils/pushNotifications';
import { useAuth } from '../context/AuthContext';

function NotificationBell() {
  const { user } = useAuth();
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    checkSubscription();
    setSupported(isPushSupported());
  }, [user]);

  const checkSubscription = async () => {
    if (!user) {
      setSubscribed(false);
      return;
    }
    const sub = await isSubscribed();
    setSubscribed(sub);
  };

  const handleClick = async () => {
    if (!user) {
      toast.error('Bitte melden Sie sich an, um Benachrichtigungen zu aktivieren');
      return;
    }
    if (!supported) {
      toast.error('Push-Benachrichtigungen werden von diesem Browser nicht unterstützt');
      return;
    }

    setLoading(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
        toast.success('Benachrichtigungen deaktiviert');
      } else {
        await subscribeToPush();
        setSubscribed(true);
        toast.success('Sie erhalten jetzt Breaking News Benachrichtigungen! 🔔');
      }
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Fehler beim Aktivieren der Benachrichtigungen';
      if (msg.includes('permission denied')) {
        toast.error('Berechtigung verweigert. Bitte erlauben Sie Benachrichtigungen in Ihrem Browser.');
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const permission = getPermission();
  const denied = permission === 'denied';

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleClick}
      disabled={loading || !supported}
      className="rounded-full relative"
      data-testid="notification-bell"
      title={
        !supported
          ? 'Nicht unterstützt'
          : denied
          ? 'Benachrichtigungen blockiert'
          : subscribed
          ? 'Benachrichtigungen aktiv - klicken zum Deaktivieren'
          : 'Breaking News Benachrichtigungen aktivieren'
      }
    >
      {subscribed ? (
        <BellRing className="h-5 w-5 text-[#0055FF] dark:text-[#3377FF]" />
      ) : denied ? (
        <BellOff className="h-5 w-5 text-gray-400" />
      ) : (
        <Bell className="h-5 w-5" />
      )}
      {subscribed && (
        <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
      )}
    </Button>
  );
}

export default NotificationBell;
